import React from 'react';
import { 
  Award, BookOpen, Scale, Users, Building2, Globe2, 
  Shield, Clock, MapPin, Phone, Target, Heart, 
  TrendingUp, CheckCircle, Star, Briefcase 
} from 'lucide-react';
import LLSEOMeta from '../LLSEOMeta';

const LLAboutPage = () => {

  const stats = [
    { 
      id: 1, 
      label: 'Years in Kollam', 
      value: '7+',
      description: 'Serving Kollam District'
    },
    { 
      id: 2, 
      label: 'Successful Cases', 
      value: '100+',
      description: 'Across Kerala Courts'
    },
    { 
      id: 3, 
      label: 'Client Satisfaction', 
      value: '98%',
      description: 'Happy Clients in Kollam'
    },
    { 
      id: 4, 
      label: 'Expert Attorneys', 
      value: '5+',
      description: 'Bar Council Certified'
    },
  ];

  const values = [
    {
      icon: <Scale className="w-8 h-8 text-blue-400" />,
      title: 'Legal Excellence in Kollam',
      description: 'We maintain the highest standards of legal practice in Kollam District Court and Kerala High Court, ensuring expert representation for every client with proven success rates.'
    },
    {
      icon: <Users className="w-8 h-8 text-blue-400" />,
      title: 'Client-Focused Approach',
      description: 'Your success is our priority. We provide personalized attention and tailored legal solutions to clients across Kollam district with multilingual support in English, Malayalam, and Hindi.'
    },
    {
      icon: <BookOpen className="w-8 h-8 text-blue-400" />,
      title: 'Continuous Legal Education',
      description: 'Our team stays current with evolving Indian law and Kerala-specific legal developments to provide cutting-edge counsel in Criminal, Civil, and Family law matters.'
    },
    {
      icon: <Award className="w-8 h-8 text-blue-400" />,
      title: 'Proven Track Record',
      description: 'Our history of 100+ successful outcomes in Kollam courts speaks to our commitment to excellence. 92% success rate in criminal defense cases across Kerala.'
    },
    {
      icon: <Shield className="w-8 h-8 text-blue-400" />,
      title: 'Trusted Legal Advisors',
      description: 'Trusted by businesses and individuals across Kollam for 7+ years. Deep understanding of local court procedures and Kerala legal system.'
    },
    {
      icon: <Heart className="w-8 h-8 text-blue-400" />,
      title: 'Community Commitment',
      description: 'Deeply rooted in Kollam community. Providing accessible legal services to families and businesses across Kerala with compassionate representation.'
    }
  ];

  const achievements = [
    {
      icon: <Briefcase className="w-6 h-6 text-green-400" />,
      title: "7+ Years Serving Kollam",
      description: "Established legal practice in Kollam since 2019, with lawyers having 7+ years combined experience"
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-400" />,
      title: "100+ Cases Won",
      description: "Successfully resolved over 100 cases in Kollam District Court, Family Courts, and Kerala High Court"
    },
    {
      icon: <Star className="w-6 h-6 text-green-400" />,
      title: "4.8/5 Client Rating",
      description: "Consistently rated as one of the best law firms in Kollam with 250+ positive reviews"
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-green-400" />,
      title: "92% Success Rate",
      description: "Exceptional success rate in criminal defense and civil litigation matters across Kerala"
    }
  ];

  const practiceHighlights = [
    {
      area: "Criminal Law",
      cases: "100+",
      rate: "92%",
      description: "Expert defense in Kollam District Court"
    },
    {
      area: "Civil Litigation",
      cases: "100+",
      rate: "95%",
      description: "Property disputes & contracts"
    },
    {
      area: "Family Law",
      cases: "150+",
      rate: "90%",
      description: "Divorce, custody & maintenance"
    },
    {
      area: "Cyber Law",
      cases: "100+",
      rate: "88%",
      description: "Online fraud & IT Act cases"
    }
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <LLSEOMeta 
        title="About Us – Best Law Firm in Kollam, Kerala"
        description="Learn about LoopHoleLaw, Kollam's premier law firm with 7+ years experience, 100+ successful cases, and 98% client satisfaction. Expert lawyers in Criminal, Civil, Family & Property Law. Serving Kollam District Court & Kerala High Court. Call: +91 9539650421"
        keywords="about LoopHoleLaw, best law firm Kollam, top lawyers Kollam Kerala, legal services Kollam, experienced lawyers Kerala, Kollam attorneys, കൊല്ലം നിയമ സ്ഥാപനം, criminal lawyers Kollam, civil lawyers Kerala, family law Kollam"
        canonicalPath="/about"
        schema={JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "LegalService",
              "@id": "https://loopholelaw.com/#organization",
              "name": "LoopHoleLaw",
              "alternateName": "LoopHoleLaw Advocates",
              "description": "Premier law firm in Kollam, Kerala with 7+ years of experience. Specializing in Criminal Law, Civil Litigation, Cyber Law, Family Law, Property Law, and Corporate Law.",
              "url": "https://loopholelaw.com",
              "telephone": "+919539650421",
              "email": "loopholelawcontact@gmail.com",
              "foundingDate": "2019",
              "priceRange": "$$",
              "slogan": "Navigating Legal Complexities with Precision and Innovation",
              "knowsLanguage": ["English", "Malayalam", "Hindi"],
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "1st Floor, Ganga Building, Opposite Kollam Collectorate",
                "addressLocality": "Kollam",
                "addressRegion": "Kerala",
                "postalCode": "691013",
                "addressCountry": "IN"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": "8.892817793613391",
                "longitude": "76.57290401478547"
              },
              "openingHoursSpecification": [
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  "opens": "09:00",
                  "closes": "18:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": "Saturday",
                  "opens": "09:00",
                  "closes": "14:00"
                }
              ],
              "areaServed": [
                { "@type": "City", "name": "Kollam" },
                { "@type": "City", "name": "Karunagappally" },
                { "@type": "City", "name": "Sasthamcotta" },
                { "@type": "City", "name": "Kottarakkara" },
                { "@type": "City", "name": "Punalur" }
              ],
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.8",
                "reviewCount": "250",
                "bestRating": "5",
                "worstRating": "1"
              },
              "sameAs": [
                "https://www.facebook.com/loopholelaw",
                "https://www.linkedin.com/company/loopholelaw"
              ]
            },
            {
              "@type": "AboutPage",
              "@id": "https://loopholelaw.com/about",
              "url": "https://loopholelaw.com/about",
              "name": "About LoopHoleLaw – Best Law Firm in Kollam",
              "description": "Learn about LoopHoleLaw, Kollam's premier legal service provider with 7+ years experience, 100+ successful cases, and 98% client satisfaction rate."
            }
          ]
        })}
      />

      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-black pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-green-500/10 rounded-full border border-green-500/30">
              <MapPin className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm">Kollam's Trusted Legal Partner Since 2019</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              About LoopHoleLaw
            </h1>
            <p className="text-2xl md:text-3xl text-blue-400 mb-6">
              കൊല്ലത്തെ വിശ്വസ്ത നിയമ സ്ഥാപനം
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Kollam's premier law firm combining 7+ years of legal expertise, innovative solutions, 
              and unwavering dedication to deliver exceptional legal services across Kerala. 
              Trusted by 100+ clients in Kollam district.
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-gray-300">
                <Shield className="w-5 h-5 text-green-400" />
                <span>Bar Council of Kerala Certified</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Award className="w-5 h-5 text-green-400" />
                <span>7+ Years in Kollam</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Star className="w-5 h-5 text-green-400" />
                <span>4.8/5 Rating • 250+ Reviews</span>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat) => (
              <div 
                key={stat.id} 
                className="text-center bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-green-400/50 transition-all"
              >
                <div className="text-4xl md:text-5xl font-bold text-green-400 mb-2">
                  {stat.value}
                </div>
                <div className="text-base font-semibold text-white mb-1">
                  {stat.label}
                </div>
                <div className="text-xs text-gray-400">
                  {stat.description}
                </div>
              </div>
            ))}
          </div>

          {/* Our Story Section */}
          <div className="mt-24">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
                Our Story: Building Kollam's Leading Law Firm
              </h2>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 space-y-6">
                <p className="text-gray-300 text-lg leading-relaxed">
                  <strong className="text-white">LoopHoleLaw</strong> was founded in Kollam, Kerala with a vision 
                  to provide accessible, high-quality legal services to individuals and businesses across the district. 
                  What started as a small practice near Kollam Collectorate has grown into one of the most trusted 
                  law firms in Kerala.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Our team of experienced attorneys, all graduates of <strong className="text-white">Sree Narayana Guru 
                  College of Legal Studies, Kollam</strong>, brings deep expertise in Criminal Law, Civil Litigation, 
                  Cyber Law, Family Law, and Property disputes. With 7+ years of combined experience practicing in 
                  <strong className="text-white"> Kollam District Court</strong> and 
                  <strong className="text-white"> Kerala High Court</strong>, we've successfully resolved over 100 cases.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Today, we serve clients across <strong className="text-white">Kollam, Karunagappally, Sasthamcotta, 
                  Kottarakkara, Punalur, and surrounding areas</strong>, offering legal services in 
                  <strong className="text-white"> English, Malayalam (മലയാളം), and Hindi</strong> to ensure comfortable 
                  communication for all our clients.
                </p>
              </div>
            </div>
          </div>

          {/* Achievements Grid */}
          <div className="mt-24">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Our Achievements in Kollam Legal Practice
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {achievements.map((achievement, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-xl p-6 hover:border-green-400/50 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-green-500/10 rounded-lg p-3 flex-shrink-0">
                      {achievement.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">
                        {achievement.title}
                      </h3>
                      <p className="text-gray-400">
                        {achievement.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mission Section */}
          <div className="mt-24">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Our Mission: Serving Kollam with Legal Excellence
              </h2>
              <div className="bg-gradient-to-r from-blue-500/10 to-green-500/10 border border-slate-700 rounded-xl p-8">
                <p className="text-gray-300 text-lg leading-relaxed mb-4">
                  At <strong className="text-white">LoopHoleLaw</strong>, our mission is to provide 
                  <strong className="text-green-400"> accessible, expert legal services</strong> to the people 
                  of Kollam and across Kerala. We are committed to maintaining the highest standards of 
                  professional excellence while ensuring justice is within reach for everyone.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  We empower our clients with strategic legal counsel that drives their success, protects 
                  their interests, and upholds their rights in <strong className="text-white">Kollam District Court, 
                  Kerala High Court, and all Kerala courts</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Practice Area Highlights */}
          <div className="mt-24">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Our Practice Areas in Kollam
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {practiceHighlights.map((practice, index) => (
                <div
                  key={index}
                  className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:bg-slate-800/70 hover:border-green-400/50 transition-all text-center"
                >
                  <h3 className="text-xl font-bold text-white mb-3">{practice.area}</h3>
                  <div className="flex justify-center gap-4 mb-3">
                    <div>
                      <div className="text-2xl font-bold text-green-400">{practice.cases}</div>
                      <div className="text-xs text-gray-400">Cases</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-400">{practice.rate}</div>
                      <div className="text-xs text-gray-400">Success</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-400">{practice.description}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <a
                href="/practice-areas"
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors"
              >
                View All Practice Areas
                <Scale className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Values Section */}
          <div className="mt-24">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
              Our Core Values & Principles
            </h2>
            <p className="text-center text-gray-400 mb-12 max-w-3xl mx-auto">
              The foundation of our legal practice in Kollam, Kerala
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <div 
                  key={index} 
                  className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 hover:bg-slate-800/70 hover:border-green-400/50 transition-all group"
                >
                  <div className="bg-blue-500/10 rounded-lg w-16 h-16 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Service Areas */}
          <div className="mt-24">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white text-center mb-6">
                Serving All of Kollam District
              </h2>
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  'Kollam City', 'Karunagappally', 'Sasthamcotta', 
                  'Kottarakkara', 'Punalur', 'Chavara', 
                  'Paravur', 'Anchal', 'Kundara', 'Pathanapuram',
                  'Kottiyam', 'Oachira', 'Mynagappally', 'Perinad'
                ].map((area) => (
                  <span
                    key={area}
                    className="px-4 py-2 bg-green-500/10 text-green-400 rounded-full text-sm border border-green-500/30"
                  >
                    {area}
                  </span>
                ))}
              </div>
              <p className="text-center text-gray-400 mt-6">
                <strong className="text-white">Office Location:</strong> 1st Floor, Ganga Building, 
                Opposite Kollam Collectorate, Kollam - 691013, Kerala
              </p>
            </div>
          </div>

          {/* Team Section */}
          <div className="mt-24">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Meet Our Expert Legal Team in Kollam
              </h2>
              <p className="text-gray-300 text-lg mb-8">
                Our team of experienced attorneys brings diverse expertise across multiple practice areas. 
                All our lawyers are <strong className="text-white">Bar Council of Kerala certified</strong> with 
                extensive experience in <strong className="text-white">Kollam District Court</strong> and 
                <strong className="text-white"> Kerala High Court</strong>.
              </p>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <Users className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">5+ Expert Attorneys</h3>
                  <p className="text-gray-400 text-sm">
                    Specialized in Criminal, Civil, Family & Cyber Law
                  </p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <Award className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">7+ Years Experience</h3>
                  <p className="text-gray-400 text-sm">
                    Combined experience in Kerala courts
                  </p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <Globe2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Multilingual Team</h3>
                  <p className="text-gray-400 text-sm">
                    English, Malayalam & Hindi speaking lawyers
                  </p>
                </div>
              </div>
              <a
                href="/team"
                className="inline-flex items-center gap-2 px-8 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
              >
                Meet Our Attorneys
                <Users className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="mt-24">
            <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-700 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-white text-center mb-8">
                Why Choose LoopHoleLaw in Kollam?
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    icon: <MapPin className="w-6 h-6" />,
                    title: "Prime Location in Kollam",
                    text: "Conveniently located opposite Kollam Collectorate, near District Court"
                  },
                  {
                    icon: <Clock className="w-6 h-6" />,
                    title: "24/7 Emergency Support",
                    text: "Available round-the-clock for urgent legal matters across Kollam"
                  },
                  {
                    icon: <Target className="w-6 h-6" />,
                    title: "High Success Rate",
                    text: "92% success rate in criminal cases, 95% in civil litigation"
                  },
                  {
                    icon: <Heart className="w-6 h-6" />,
                    title: "Client-Centric Approach",
                    text: "Personalized attention and transparent communication throughout"
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="bg-green-500/10 rounded-lg p-3 text-green-400 flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                      <p className="text-gray-400 text-sm">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-24">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Frequently Asked Questions About LoopHoleLaw
            </h2>
            <div className="max-w-4xl mx-auto space-y-6">
              {[
                {
                  q: "Where is LoopHoleLaw located in Kollam?",
                  a: "We are located at 1st Floor, Ganga Building, Opposite Kollam Collectorate, Kollam - 691013, Kerala. Our office is conveniently situated near Kollam District Court, making it easily accessible for clients across Kollam district."
                },
                {
                  q: "What areas of law does LoopHoleLaw specialize in?",
                  a: "We specialize in Criminal Law, Civil Litigation, Cyber Law, Family Law, Property Law, Corporate Law, Consumer Law, and Banking Law. Our team has extensive experience handling cases in Kollam District Court and Kerala High Court."
                },
                {
                  q: "How many years of experience does LoopHoleLaw have?",
                  a: "Our law firm has been serving Kollam since 2019, with our attorneys bringing 7+ years of combined legal experience in Kerala courts. We've successfully handled over 100 cases with a 92% success rate in criminal defense."
                },
                {
                  q: "Do you provide legal services in Malayalam?",
                  a: "Yes! All our attorneys are fluent in Malayalam (മലയാളം), English, and Hindi. We ensure comfortable communication for all clients across Kerala in their preferred language."
                },
                {
                  q: "What is your success rate in legal cases?",
                  a: "We maintain an impressive track record with a 92% success rate in criminal defense cases, 95% in civil litigation, and 90% in family law matters. We've won over 100 cases across Kollam and Kerala courts."
                }
              ].map((faq, index) => (
                <div
                  key={index}
                  className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-green-400/50 transition-all"
                >
                  <h3 className="text-lg font-semibold text-white mb-3">
                    {faq.q}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-24 text-center">
            <div className="bg-gradient-to-r from-blue-500/10 to-green-500/10 border border-slate-700 rounded-xl p-12 max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Work with Kollam's Best Law Firm?
              </h2>
              <p className="text-xl text-gray-300 mb-2">
                കൊല്ലത്തെ മികച്ച നിയമ സ്ഥാപനവുമായി പ്രവർത്തിക്കാൻ തയ്യാറാണോ?
              </p>
              <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
                Contact us today to schedule a free consultation with our expert legal team. 
                We're here to protect your rights and interests in all legal matters.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/contact" 
                  className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-4 rounded-lg transition-all shadow-lg hover:shadow-green-500/50 text-lg"
                >
                  <Phone className="w-5 h-5" />
                  Schedule Free Consultation
                </a>
                <a 
                  href="tel:+919539650421" 
                  className="inline-flex items-center justify-center gap-2 border-2 border-white hover:bg-white/10 text-white font-semibold px-8 py-4 rounded-lg transition-all text-lg"
                >
                  <Phone className="w-5 h-5" />
                  Call +91 9539650421
                </a>
              </div>
              <p className="text-gray-500 mt-6">
                <MapPin className="w-4 h-4 inline mr-2" />
                Opposite Kollam Collectorate • Near District Court • Easy Parking Available
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LLAboutPage;